import { ProjectSessionStorage, type ProjectSession } from './ProjectSessionStorage'
import { confirm_action } from '../../lib/ConfirmationDialog.ts'
import { ModalDialog } from '../../lib/ModalDialog.ts'
import { escape_html } from '../../lib/html.ts'

/**
 * Renders the "Resume project" list on the LoadModel step. Populated from
 * IndexedDB; each row has a thumbnail, the session name, the step the user
 * was on, and Resume / Delete buttons.
 *
 * The whole panel is hidden when there are no saved sessions so first-time
 * users don't see an empty UI element.
 */

export interface ProjectSessionsPanelOptions {
  panel: HTMLElement
  list: HTMLElement
  on_resume: (id: string) => Promise<void> | void
}

export class ProjectSessionsPanel {
  constructor (private readonly options: ProjectSessionsPanelOptions) {}

  public async refresh (): Promise<void> {
    const sessions = await ProjectSessionStorage.list()
    if (sessions.length === 0) {
      this.options.panel.style.display = 'none'
      return
    }
    this.options.panel.style.display = 'flex'
    this.options.list.innerHTML = ''
    for (const session of sessions) {
      this.options.list.appendChild(this.render_row(session))
    }
  }

  private render_row (session: ProjectSession): HTMLElement {
    const row = document.createElement('div')
    row.style.display = 'flex'
    row.style.alignItems = 'center'
    row.style.gap = '0.5rem'
    row.style.padding = '0.25rem'
    row.style.border = '1px solid rgba(127,127,127,0.3)'
    row.style.borderRadius = '4px'

    const thumb = document.createElement('div')
    thumb.style.width = '64px'
    thumb.style.height = '48px'
    thumb.style.flex = '0 0 auto'
    thumb.style.background = '#222'
    thumb.style.borderRadius = '3px'
    thumb.style.backgroundSize = 'cover'
    thumb.style.backgroundPosition = 'center'
    if (session.thumbnail_data_url !== undefined) {
      thumb.style.backgroundImage = `url("${session.thumbnail_data_url}")`
    }
    row.appendChild(thumb)

    const meta = document.createElement('div')
    meta.style.flex = '1'
    meta.style.minWidth = '0'
    meta.style.display = 'flex'
    meta.style.flexDirection = 'column'

    const name = document.createElement('span')
    name.textContent = session.name
    name.style.fontWeight = '600'
    name.style.whiteSpace = 'nowrap'
    name.style.overflow = 'hidden'
    name.style.textOverflow = 'ellipsis'
    meta.appendChild(name)

    const sub = document.createElement('span')
    sub.style.fontSize = '0.8em'
    sub.style.opacity = '0.75'
    sub.textContent = `${pretty_step(session.last_step)} · ${pretty_date(session.updated_at)}`
    meta.appendChild(sub)

    row.appendChild(meta)

    const actions = document.createElement('div')
    actions.style.display = 'flex'
    actions.style.gap = '0.25rem'

    const resume_btn = document.createElement('button')
    resume_btn.className = 'secondary-button'
    resume_btn.textContent = 'Resume'
    resume_btn.addEventListener('click', () => {
      void this.options.on_resume(session.id)
    })
    actions.appendChild(resume_btn)

    const delete_btn = document.createElement('button')
    delete_btn.className = 'no-style-button'
    delete_btn.title = 'Delete saved session'
    delete_btn.textContent = '🗑'
    delete_btn.addEventListener('click', () => {
      void confirm_action({
        title: 'Delete saved project',
        message: `Delete saved project "${session.name}"?`,
        confirm_label: 'Delete',
        danger: true
      }).then(async (confirmed) => {
        if (!confirmed) return
        const deleted = await ProjectSessionStorage.delete(session.id)
        if (!deleted) {
          new ModalDialog('Delete failed', delete_saved_project_failure_message(session.name)).show()
          return
        }
        await this.refresh()
      })
    })
    actions.appendChild(delete_btn)

    row.appendChild(actions)
    return row
  }
}

export function delete_saved_project_failure_message (name: string): string {
  return `Could not delete saved project "${escape_html(name)}". Your browser storage may be unavailable or blocked.`
}

function pretty_step (step: string): string {
  switch (step) {
    case 'load-model': return 'Load Model'
    case 'load-skeleton': return 'Load Skeleton'
    case 'build-skeleton': return 'Build Skeleton'
    case 'edit-skeleton': return 'Edit Skeleton'
    case 'weight-skin': return 'Weight Skin'
    case 'animations-export': return 'Animations'
    case 'BindPose': return 'Bind Pose'
    default: return step
  }
}

function pretty_date (iso: string): string {
  try {
    const d = new Date(iso)
    const today = new Date()
    const same_day = d.toDateString() === today.toDateString()
    if (same_day) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    return d.toLocaleDateString()
  } catch (_) {
    return iso
  }
}

